import { motion } from "framer-motion";
import { Check, MagnifyingGlass, PaperPlaneTilt, ListChecks } from "@phosphor-icons/react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import SubpageHero from "@/components/site/SubpageHero";
import ReviewForm from "@/components/site/ReviewForm";
import FAQ from "@/components/site/FAQ";
import useSeo from "@/hooks/useSeo";
import { FAQS, faqJsonLd } from "@/lib/faqs";

const REVIEW_FAQS = FAQS.filter((f) => f.rev);

const covers = [
  "Homepage and mobile layout improvements",
  "New service pages, galleries, and updated content",
  "Better contact and quote forms",
  "Optional quote & call automations — text alerts to you, acknowledgments to your customer, missed-call text backs",
];

const looksAt = [
  ["How it reads on a phone", "Most of your customers arrive on mobile. We check whether your homepage, menus, and buttons actually work with one thumb."],
  ["How easy it is to get a quote", "How many taps it takes to reach you, whether your form is clear, and whether anything silently fails."],
  ["What your pages say", "Whether your services, areas, and pricing are obvious in the first few seconds — or buried."],
  ["What it's built on", "The platform and hosting behind it. Some platforms we can improve in place, others are too locked down — we tell you honestly which yours is."],
];

const steps = [
  [MagnifyingGlass, "Send the link", "Drop your website address in the form. No call required, nothing to install, nothing to pay."],
  [ListChecks, "We review it", "We look at the site ourselves — on a phone and a desktop — and note anything that could be making it harder for customers to contact you."],
  [PaperPlaneTilt, "You get a plan", "Within one business day you get a short, plain-English list of what we'd change and what it would cost. Going ahead is entirely up to you."],
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "@id": "https://webjesters.com/website-review#service",
      name: "Website review and improvement",
      serviceType: "Website redesign and improvement for local service businesses",
      provider: { "@id": "https://webjesters.com/#organization" },
      areaServed: { "@type": "Country", name: "United States" },
      url: "https://webjesters.com/website-review",
      description:
        "WebJesters reviews an existing local business website for free and improves it instead of rebuilding from scratch — homepage and mobile layout improvements, new service pages and galleries, updated content, better contact and quote forms, and optional quote and call automations. The initial review costs nothing. Improvement projects receive a flat quote after reviewing the website and the platform it is built on, a one-time project is fine, and ongoing management is optional and agreed separately.",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "WebJesters", item: "https://webjesters.com/" },
        { "@type": "ListItem", position: 2, name: "Website review", item: "https://webjesters.com/website-review" },
      ],
    },
    faqJsonLd(REVIEW_FAQS),
  ],
};

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 text-[11.5px] tracking-[0.22em] uppercase text-brand-700">
    <span className="block w-8 h-px bg-brand-700" />
    {children}
  </div>
);

export default function WebsiteReviewPage() {
  useSeo({
    title: "Request a Free Website Review — Improve Your Existing Site | WebJesters",
    description:
      "Already have a website? We'll review it free on phone and desktop and reply within one business day with what we'd fix and what it costs.",
    path: "/website-review",
    jsonLd: JSON_LD,
  });

  return (
    <main id="main-content" data-testid="website-review-page" className="relative">
      <Navbar />

      <SubpageHero
        crumb="Website review"
        eyebrow="For businesses that already have a site"
        title="Request a free website review."
        intro="You don't need to start over. Send us your website and we'll point out anything that could be making it harder for customers to contact you — then improve it: refresh the design, make it work on mobile, update your content, or simplify how customers request a quote."
      >
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[13.5px] text-zinc-300">
          <span className="inline-flex items-center gap-2">
            <span className="block w-1.5 h-1.5 rounded-full bg-emerald-400" />
            Free, no obligation
          </span>
          <span className="hidden sm:inline text-zinc-600">·</span>
          <span>Reply within one business day</span>
          <span className="hidden sm:inline text-zinc-600">·</span>
          <span>No call required</span>
          <span className="hidden sm:inline text-zinc-600">·</span>
          <span>Quoted per project, nothing automatic</span>
        </div>
      </SubpageHero>

      {/* Form + what it covers */}
      <section
        id="review"
        data-tone="light"
        data-testid="review-section"
        className="tone-light relative py-12 sm:py-20 bg-[#f6f5f1] border-t border-zinc-200 scroll-mt-16 md:scroll-mt-20"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <ReviewForm />
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <Eyebrow>What this can cover</Eyebrow>
            <ul className="mt-6 grid gap-3.5">
              {covers.map((c, i) => (
                <motion.li
                  key={c}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`review-cover-${i}`}
                  className="flex items-start gap-3 text-[14.5px] text-zinc-700 leading-relaxed"
                >
                  <Check size={15} weight="bold" className="text-brand-700 mt-[3px] flex-shrink-0" />
                  {c}
                </motion.li>
              ))}
            </ul>
            <p
              data-testid="review-quote-note"
              className="mt-7 pt-6 border-t border-zinc-200 text-[13px] text-zinc-600 leading-relaxed"
            >
              The review itself is free. Improvement projects get a flat quote
              after we've looked at your website and the platform it's built on
              — and if it can't be improved in place, we'll say so instead of
              charging you to find out. A one-time project is fine; ongoing
              management is optional and agreed separately. Our $499 build plus
              $79/month plan stays its own offer if you'd rather start fresh.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section data-tone="light" className="tone-light relative py-14 sm:py-24 bg-white border-y border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-[30px] sm:text-4xl lg:text-[44px] leading-[1.05] tracking-tight text-zinc-900">
            How the review works.
          </h2>
          <ol className="relative mt-10 sm:mt-14 grid md:grid-cols-3 gap-8 md:gap-6">
            <span aria-hidden className="hidden md:block absolute top-6 left-[17%] right-[17%] h-px bg-zinc-200" />
            {steps.map(([Icon, h, p], i) => (
              <motion.li
                key={h}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`review-step-${i}`}
                className="relative"
              >
                <span className="relative z-10 inline-flex items-center justify-center w-12 h-12 rounded-full bg-zinc-900 text-white ring-8 ring-white">
                  <Icon size={19} weight="regular" />
                </span>
                <div className="mt-5 text-[11px] tracking-[0.22em] uppercase text-brand-700">
                  Step {i + 1}
                </div>
                <h3 className="mt-1.5 font-display font-bold text-xl sm:text-[22px] tracking-tight text-zinc-900">
                  {h}
                </h3>
                <p className="mt-3 text-[14.5px] text-zinc-600 leading-relaxed max-w-[40ch]">{p}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* What we look at */}
      <section data-tone="light" className="tone-light relative py-14 sm:py-24 bg-[#f6f5f1]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <Eyebrow>The review</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-[30px] sm:text-4xl lg:text-[44px] leading-[1.05] tracking-tight text-zinc-900 max-w-2xl">
            What we actually look at.
          </h2>
          <div className="mt-8 sm:mt-12 grid sm:grid-cols-2 gap-4">
            {looksAt.map(([h, p], i) => (
              <motion.div
                key={h}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`review-look-${i}`}
                className="card-light rounded-3xl p-6 sm:p-7"
              >
                <div className="font-mono text-[12px] tracking-[0.2em] text-brand-700">0{i + 1}</div>
                <h3 className="mt-3 text-[16px] font-medium text-zinc-900 leading-snug">{h}</h3>
                <p className="mt-2.5 text-[14px] text-zinc-600 leading-relaxed">{p}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 sm:mt-14 rounded-3xl bg-zinc-900 text-white p-7 sm:p-9 relative overflow-hidden">
            <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-brand-500/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-white">
                Rather start fresh?
              </h3>
              <p className="mt-3 text-[14.5px] text-zinc-300 leading-relaxed max-w-2xl">
                If your current site isn't worth saving, we build, host, and
                maintain a new one for $499 plus $79/month — including hosting,
                SSL, lead alerts to your email, and a client portal where you can
                update your own content.
              </p>
              <a
                href="/#pricing"
                data-testid="review-to-pricing"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-[13.5px] font-medium hover:bg-zinc-200 transition-colors"
              >
                See the website plan →
              </a>
            </div>
          </div>
        </div>
      </section>

      <FAQ
        items={REVIEW_FAQS}
        tone="light"
        title="Website review questions."
        askHref="#review"
        intro="Straight answers on what the review costs, what platforms we can work with, and how improvement projects are priced."
      />
      <Footer />
    </main>
  );
}
